import { useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { useForm, useFieldArray } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { useTranslation } from "react-i18next"
import { ArrowLeft, Plus, Trash2 } from "lucide-react"
import { useContact, useCreateContact, useUpdateContact } from "@/hooks/use-contacts"
import { cn } from "@/lib/utils"
import { ApiClientError } from "@/lib/api-client"
import { toast } from "sonner"

const contactSchema = z.object({
  first_name: z.string().min(1, "Imię jest wymagane"),
  last_name: z.string().min(1, "Nazwisko jest wymagane"),
  title: z.string().optional(),
  department: z.string().optional(),
  gdpr_marketing_consent: z.boolean().optional(),
  emails: z.array(z.object({
    email: z.string().email("Nieprawidłowy email"),
    is_primary: z.boolean(),
    label: z.string().optional(),
  })),
  phones: z.array(z.object({
    number: z.string().min(1, "Numer jest wymagany"),
    is_primary: z.boolean(),
    label: z.string().optional(),
  })),
})

type ContactForm = z.infer<typeof contactSchema>

export function ContactFormPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { id } = useParams()
  const isEdit = !!id
  const [error, setError] = useState<string | null>(null)

  const { data: contactData } = useContact(id)
  const createMutation = useCreateContact()
  const updateMutation = useUpdateContact()

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = useForm<ContactForm>({
    resolver: zodResolver(contactSchema),
    values: isEdit && contactData ? {
      first_name: contactData.data.first_name,
      last_name: contactData.data.last_name,
      title: contactData.data.title || "",
      department: contactData.data.department || "",
      gdpr_marketing_consent: contactData.data.gdpr_marketing_consent,
      emails: contactData.data.emails.map((e) => ({
        email: e.email, is_primary: e.is_primary, label: e.label || "",
      })),
      phones: contactData.data.phones.map((p) => ({
        number: p.number, is_primary: p.is_primary, label: p.label || "",
      })),
    } : {
      first_name: "", last_name: "", emails: [], phones: [],
    },
  })

  const emailFields = useFieldArray({ control, name: "emails" })
  const phoneFields = useFieldArray({ control, name: "phones" })

  const onSubmit = async (data: ContactForm) => {
    try {
      setError(null)
      if (isEdit) {
        await updateMutation.mutateAsync({ id, data })
        toast.success("Kontakt zaktualizowany")
      } else {
        await createMutation.mutateAsync(data)
        toast.success("Kontakt utworzony")
      }
      navigate("/contacts")
    } catch (err) {
      if (err instanceof ApiClientError) setError(err.message)
      else setError("Wystąpił nieoczekiwany błąd")
    }
  }

  const inputClass = "h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm text-foreground outline-none"

  return (
    <div className="mx-auto max-w-[720px] space-y-6">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted/50">
          <ArrowLeft size={18} />
        </button>
        <h1 className="text-xl font-semibold text-foreground">
          {isEdit ? "Edytuj osobę" : t("contacts.new")}
        </h1>
      </div>

      {error && (
        <div className="rounded-lg px-4 py-3 text-sm bg-error-light text-error-dark">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <section className="space-y-4">
          <h2 className="text-sm font-semibold uppercase text-muted-foreground">Dane osobowe</h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-muted-foreground">{t("contacts.firstName")} *</label>
              <input {...register("first_name")} className={cn(inputClass, errors.first_name && "border-error")} />
              {errors.first_name && <p className="text-xs text-error">{errors.first_name.message}</p>}
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-muted-foreground">{t("contacts.lastName")} *</label>
              <input {...register("last_name")} className={cn(inputClass, errors.last_name && "border-error")} />
              {errors.last_name && <p className="text-xs text-error">{errors.last_name.message}</p>}
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-muted-foreground">{t("contacts.jobTitle")}</label>
              <input {...register("title")} className={inputClass} />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-muted-foreground">{t("contacts.department")}</label>
              <input {...register("department")} className={inputClass} />
            </div>
          </div>
        </section>

        {/* Emails */}
        <section className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold uppercase text-muted-foreground">Email</h2>
            <button type="button" onClick={() => emailFields.append({ email: "", is_primary: emailFields.fields.length === 0, label: "" })}
              className="inline-flex items-center gap-1 text-xs text-primary">
              <Plus size={14} /> Dodaj
            </button>
          </div>
          {emailFields.fields.map((field, index) => (
            <div key={field.id} className="flex items-center gap-2">
              <input {...register(`emails.${index}.email`)} placeholder="email@example.com" className={cn(inputClass, "flex-1", errors.emails?.[index]?.email && "border-error")} />
              <input {...register(`emails.${index}.label`)} placeholder="Etykieta" className={`${inputClass} w-28`} />
              <button type="button" onClick={() => emailFields.remove(index)} className="p-1.5 text-muted-foreground">
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </section>

        {/* Phones */}
        <section className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold uppercase text-muted-foreground">Telefon</h2>
            <button type="button" onClick={() => phoneFields.append({ number: "", is_primary: phoneFields.fields.length === 0, label: "" })}
              className="inline-flex items-center gap-1 text-xs text-primary">
              <Plus size={14} /> Dodaj
            </button>
          </div>
          {phoneFields.fields.map((field, index) => (
            <div key={field.id} className="flex items-center gap-2">
              <input {...register(`phones.${index}.number`)} placeholder="+48..." className={cn(inputClass, "flex-1", errors.phones?.[index]?.number && "border-error")} />
              <input {...register(`phones.${index}.label`)} placeholder="Etykieta" className={`${inputClass} w-28`} />
              <button type="button" onClick={() => phoneFields.remove(index)} className="p-1.5 text-muted-foreground">
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </section>

        {/* GDPR */}
        <section className="space-y-3">
          <h2 className="text-sm font-semibold uppercase text-muted-foreground">RODO</h2>
          <label className="flex items-center gap-2 text-sm text-muted-foreground">
            <input type="checkbox" {...register("gdpr_marketing_consent")} className="rounded" />
            {t("contacts.gdprConsent")}
          </label>
        </section>

        {/* Submit */}
        <div className="flex justify-end gap-3 border-t border-border pt-4">
          <button type="button" onClick={() => navigate(-1)}
            className="h-9 rounded-md border border-input px-4 text-sm font-medium text-foreground">
            {t("common.cancel")}
          </button>
          <button type="submit" disabled={isSubmitting}
            className="h-9 rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground disabled:opacity-50">
            {isSubmitting ? t("common.loading") : t("common.save")}
          </button>
        </div>
      </form>
    </div>
  )
}
